<template>
  <div class="saved-locations" v-if="locations.length > 0">
    <div class="saved-locations__title">
      {{ t('saved.recent-addresses') }}
    </div>

    <div v-for="location in locations.slice(0, 3)" :key="location.id">
      <Divider type="dashed"/>
      <div data-test-id="saved-location"
           :data-address-id="location.id"
           class="cursor-pointer saved-locations__address"
           @click.stop="selectLocation(location.id)">
        <Icon name="pin"/>
        <div>
          {{ location.address }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Icon from '@/components/partials/Icon.vue'
import Divider from '@/components/partials/Divider.vue'
import type { Location } from '@/types/Location'

const props = defineProps<{
  locations: Location[]
}>()

const locations = computed(() => {
  return props.locations.map(location => {
    return {
      id: location.id,
      address: [
        'street',
        'houseNumber',
        'zipcode',
        'city',
      ].reduce((acc, key) => {
        if (key === 'zipcode') {
          return `${acc}, ${location[key]}`
        }

        return `${acc} ${location[key]}`
      }, ''),
    }
  })
})

const emit = defineEmits<{
  (e: 'selectLocation', location: Location): void
}>()

const { t } = useI18n()

function selectLocation(id: number) {
  const location = props.locations.find(location => location.id === id)

  if (!location) {
    throw Error('Location not found')
  }

  emit('selectLocation', location)
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";

.saved-locations {
  &__title {
    font-weight: 700;
    margin-bottom: 1rem;
  }

  &__address {
    padding: 1rem 0;
    display: flex;
    align-items: center;

    @include icon-svg(1.5rem, 1.5rem) {
      margin-right: 0.5rem;
      color: var(--color-neutral-secondary);
    }
  }
}
</style>
